import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Role } from 'src/app/backend-client/models/Backend/Core/Infrastructure/Aggregates/Identity/role';
import { MxIdentityService } from './mx.identity.service';

/** A súgó két külön kézikönyv: a patikáké és a Pharmaroad adminé. Egyik szerepkör sem kapja meg a másikét. */
export type SzHelpAudience = 'patika' | 'admin';

const PHARMACY_ROLES = [Role.Pharmacy, Role.PharmacyBasic, Role.PartnerProgramPharmacy];
const ADMIN_ROLES = [Role.Administrator, Role.Admin];

/** Útvonal-előtag → a kézikönyv fejezete. Az első egyező sor nyer, ezért a hosszabb előtag áll elöl. */
const SECTIONS: Record<SzHelpAudience, [string, string][]> = {
  patika: [
    ['/invoice-report', 'portal-szamlak'],
    ['/invoice', 'portal-szamlak'],
    ['/np-dashboard', 'portal-forgalom'],
    ['/claims', 'portal-reklamacio'],
    ['/offers', 'portal-rendeles'],
    ['/remaining-orders', 'portal-rendeles'],
    ['/posts', 'portal-hirek'],
    ['/anthroposophy-products', 'portal-hirek'],
    ['/commitment-documents', 'portal-hirek'],
    ['/product-certificate-documents', 'portal-hirek'],
    ['/work-schedule', 'portal-helyettesites'],
    ['/partner-contacts', 'portal-szirompatika'],
    ['/partner-program-report', 'portal-szirompatika'],
    ['/partner-program-assets', 'portal-szirompatika'],
    ['/sales-report', 'portal-szirompatika'],
    ['/szirom-pont', 'portal-szirompont'],
    ['/settings', 'portal-profil'],
    ['/company-details', 'portal-cegadatok'],
    ['/issue-reports', 'portal-hibajelzes'],
    ['/issue-reports', 'portal-hibajelzes'],
    ['/', 'portal-kezdolap'],
  ],
  admin: [
    ['/loyalty/members', 'sp-tagok'],
    ['/loyalty/pharmacies', 'sp-patikak'],
    ['/loyalty/campaigns', 'sp-kampanyok'],
    ['/loyalty/trainings', 'sp-oktatasok'],
    ['/loyalty/import', 'sp-pontfeltoltes'],
    ['/loyalty/reports', 'sp-bejelentesek'],
    ['/loyalty/vouchers', 'sp-utalvanyok'],
    ['/loyalty/messages', 'sp-uzenetek'],
    ['/loyalty/balances', 'sp-egyenlegek'],
    ['/loyalty/substitutes', 'sp-helyettesitok'],
    ['/loyalty/settings', 'sp-beallitasok'],
    ['/loyalty', 'sp-tagok'],
    ['/email-templates', 'adm-email-sablonok'],
    ['/email-log', 'adm-levelnaplo'],
    ['/subscriptions', 'adm-ugyfelek'],
    ['/users/internal', 'adm-belso'],
    ['/users/pharmacy', 'adm-kulso'],
    ['/users/pharmacist', 'adm-gyogyszereszek'],
    ['/posts', 'adm-hirek'],
    ['/claims', 'adm-reklamaciok'],
    ['/issue-reports', 'adm-hibajelzesek'],
    ['/issue-reports', 'adm-hibajelzesek'],
    ['/offers/documents', 'adm-dokumentumok'],
    ['/partner-program-assets', 'adm-dokumentumok'],
    ['/commitment-documents', 'adm-dokumentumok'],
    ['/product-certificate-documents', 'adm-dokumentumok'],
    ['/settings', 'adm-profil'],
    ['/', 'adm-kezdolap'],
  ],
};

@Injectable({ providedIn: 'root' })
export class SzHelpService {
  constructor(private identityService: MxIdentityService, private router: Router) {}

  /** A bejelentkezett felhasználó kézikönyve; más szerepkörnek (logisztika, helyettesítő) nincs súgó. */
  get audience(): SzHelpAudience | null {
    const role = this.identityService.accountValue?.role;
    if (role == null) {
      return null;
    }
    if (ADMIN_ROLES.includes(role)) {
      return 'admin';
    }
    return PHARMACY_ROLES.includes(role) ? 'patika' : null;
  }

  /** Az adott oldalhoz tartozó fejezet azonosítója. */
  sectionFor(url: string): string | null {
    const audience = this.audience;
    if (!audience) {
      return null;
    }
    const path = url.split(/[?#]/)[0] || '/';
    const hit = SECTIONS[audience].find(([prefix]) => (prefix === '/' ? path === '/' : path === prefix || path.startsWith(prefix + '/')));
    return hit ? hit[1] : null;
  }

  /** Súgó megnyitása az aktuális oldal fejezeténél. */
  openForCurrentPage() {
    const url = this.router.url;
    if (url.startsWith('/sugo')) {
      return;
    }
    this.router.navigate(['/sugo'], { fragment: this.sectionFor(url) || undefined });
  }
}
